// Raporlanan değer popover'ı (M16 T23, mockup 19 rozet sözlüğü "RAPORLANAN"): hücredeki
// RAPORLANAN / SABİT rozetine tıklanınca açılır. "raporlanan · <kaynak dosya> · hesap <değer> —
// sabitle". Kaynak dosya `GET /kpi/values/:id/evidence` içindeki EN SON `import_source` bağından
// (`linkedAt` DESC — üzerine yazan dosya kazanır, B-07; lazy). Portalda (`portalSetId` verildiğinde)
// danışman ucu yerine `listPortalValueEvidence` çağrılır — aksi halde 403 CLIENT_FORBIDDEN sessizce
// "kaynak dosya bağlı değil" görünüyordu. Sabitle/kaldır → `setKpiValuePinned` → `onPinned` (grid
// yenilenir). FormulaPopover ile aynı konumlandırma/kapatma deseni (portal + fixed, 2026-10-07).

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { KpiResolvedCell, KpiResolvedIndicator } from "@mahir/shared";
import { setKpiValuePinned } from "../../api/kpi";
import { listKpiValueEvidence } from "../../api/kpi-evidence";
import { listPortalValueEvidence } from "../../api/portal-kpi";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import { formatLocator, formatReportedNumber, reportedSummary } from "../../lib/kpi-evidence-ui";
import { KPI_COPY } from "../../lib/kpi-ui";
import { btnPrimary, btnSmall } from "./kpi-form-styles";
import { placementStyle, usePopoverPlacement } from "./use-popover-placement";

export interface ReportedPopoverProps {
  indicator: KpiResolvedIndicator;
  cell: KpiResolvedCell | undefined;
  columnLabel: string;
  /** çapa hücre (viewport konumu her ölçümde okunur — kaydırmada popover takip eder) */
  getAnchor: () => HTMLElement | null;
  readOnly: boolean;
  /** Portal (müşteri) görünümü: kanıt listesi portal ucundan, set bağlamıyla. */
  portalSetId?: string | undefined;
  /** sabitleme kaydedildi — çağıran grid'i yeniler */
  onPinned: () => void;
  onClose: () => void;
  /** Danışman (2026-10-08, 8.1): "Raporlanan değeri değiştir…" → hücre editörü Kanıt sekmesi. */
  onEdit?: (() => void) | undefined;
}

const R = KPI_EVIDENCE_COPY.reported;

/** B-07: birden çok `import_source` bağı varsa en son bağlanan (üzerine yazan dosya) gösterilir. */
export function latestImportSource<T extends { relationType: string; linkedAt: string }>(
  list: readonly T[],
): T | undefined {
  const sources = list.filter((e) => e.relationType === "import_source");
  const pool = sources.length > 0 ? sources : list;
  return [...pool].sort((a, b) => b.linkedAt.localeCompare(a.linkedAt))[0];
}

export function ReportedPopover(props: ReportedPopoverProps): JSX.Element {
  const { indicator, cell, columnLabel, getAnchor, readOnly, portalSetId, onPinned, onClose } =
    props;
  const summary = reportedSummary(cell);
  const percent = indicator.valueType === "percent";
  // TODO-KPI-23: raporlanan değer XXX (değer bekleniyor) — "—" yerine XXX, hesap yoksa açıklama
  const reportedPending = cell?.reportedPending === true;
  const valueId = cell?.valueId;
  const [source, setSource] = useState<{ fileName: string; locator: string } | null | "loading">(
    valueId ? "loading" : null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const pos = usePopoverPlacement(rootRef, getAnchor, [source, error]);

  useEffect(() => {
    if (!valueId) return;
    let alive = true;
    const load = portalSetId
      ? listPortalValueEvidence(valueId, portalSetId)
      : listKpiValueEvidence(valueId);
    load
      .then((list) => {
        if (!alive) return;
        const src = latestImportSource(list);
        setSource(src ? { fileName: src.fileName, locator: formatLocator(src.locator) } : null);
      })
      .catch(() => {
        if (alive) setSource(null);
      });
    return () => {
      alive = false;
    };
  }, [valueId, portalSetId]);

  async function togglePin(): Promise<void> {
    if (!valueId || !summary) return;
    setBusy(true);
    setError(null);
    try {
      await setKpiValuePinned(valueId, !summary.pinned);
      onPinned();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : R.pinFailed);
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-label={`${R.title}: ${indicator.label}`}
      data-testid="kpi-reported-popover"
      data-placement={pos.placement}
      className="rounded-xl p-4 text-sm"
      style={{
        ...placementStyle(pos),
        width: 360,
        fontFamily: "var(--font-sans)",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-md, 0 12px 32px rgba(15,42,82,.18))",
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="font-semibold flex-1" style={{ color: "var(--color-text)" }}>
          {indicator.label} · {columnLabel}
        </div>
        <button
          type="button"
          aria-label={R.close}
          onClick={onClose}
          style={{ color: "var(--color-text-3)", minWidth: 28, minHeight: 28 }}
        >
          ✕
        </button>
      </div>
      {summary ? (
        <>
          <div
            className="text-xs"
            style={{ color: "var(--color-text-2)", lineHeight: 1.7 }}
            data-testid="kpi-reported-line"
          >
            <span
              style={{
                display: "inline-block",
                padding: "0 6px",
                borderRadius: 999,
                border: summary.pinned
                  ? "1px solid var(--color-primary)"
                  : "1px dashed var(--color-primary)",
                color: "var(--color-primary)",
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                marginRight: 6,
              }}
            >
              {summary.pinned ? R.pinnedBadge : R.badge}
            </span>
            <b style={{ color: "var(--color-text)" }} data-testid="kpi-reported-value">
              {R.reportedLabel}{" "}
              {reportedPending
                ? KPI_COPY.reported.pendingLabel
                : formatReportedNumber(summary.reported, percent)}
            </b>
            {" · "}
            {source === "loading" ? (
              <span style={{ color: "var(--color-text-3)" }}>{R.sourceLoading}</span>
            ) : source ? (
              <span>
                {source.fileName}
                {source.locator ? ` ${source.locator}` : ""}
              </span>
            ) : (
              <span style={{ color: "var(--color-text-3)" }}>{R.noSource}</span>
            )}
            {" · "}
            {summary.computed !== null ? (
              <span>
                {R.computedLabel} {formatReportedNumber(summary.computed, percent)}
                {summary.mismatch !== null ? (
                  <span style={{ color: "var(--color-warning-text)" }}>
                    {" "}
                    ({R.mismatch}{" "}
                    {summary.mismatch.toLocaleString("tr-TR", { maximumFractionDigits: 2 })})
                  </span>
                ) : null}
              </span>
            ) : (
              <span style={{ color: "var(--color-text-3)" }}>
                {reportedPending
                  ? KPI_COPY.reported.pendingHint
                  : summary.pinned
                    ? R.pinnedTitle
                    : R.usedBecauseNoCalc}
              </span>
            )}
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--color-text-3)" }}>
            {summary.pinned ? R.unpinHint : R.pinHint}
          </div>
          {error ? (
            <div role="alert" className="text-xs mt-2" style={{ color: "var(--color-error)" }}>
              {error}
            </div>
          ) : null}
          {!readOnly && valueId ? (
            <div className="flex justify-end gap-2 mt-3">
              {props.onEdit ? (
                <button
                  type="button"
                  onClick={props.onEdit}
                  data-testid="kpi-reported-edit"
                  style={btnSmall}
                >
                  {R.change}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void togglePin()}
                disabled={busy}
                aria-busy={busy || undefined}
                data-testid="kpi-reported-pin"
                style={
                  summary.pinned ? btnSmall : { ...btnPrimary, minHeight: 32, padding: "4px 12px" }
                }
              >
                {busy ? "…" : summary.pinned ? R.unpin : R.pin}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {R.noSource}
        </div>
      )}
    </div>,
    document.body,
  );
}
